import { Progress } from "antd";
import "bootstrap/dist/css/bootstrap.min.css";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom"; // Use useParams to get the id
import api from "../components/api";
import CuruMatchDescription from "../components/CuruMatchDescription";
import CustomRating from "../components/CustomRating";
import PostModel from "../components/PostModel";
import RateProduct from "../components/RateProduct";
import RatingBreakdown from "../components/RatingBreakdown";
import ReviewItem from "../components/ReviewItem";
import Spinner from "../components/Spinner";
import WishListBtn from "../components/WishListBtn";
import { FollowProvider } from "../context/FollowContext";
import "./ProductDetailsPage.css";
import ProductNotFound from "./ProductNotFoundPage";
import CuruChatsPost from "../components/CuruChatsPost";

export default function ProductDetailsPage() {
  const [productDetails, setProductDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [currentValue, setCurrentValue] = useState(0);
  const [userProfile, setUserProfile] = useState(null);
  const [profileMatch, setProfileMatch] = useState([]);
  const [isRatingSubmitted, setIsRatingSubmitted] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [isPostModelOpen, setIsPostModelOpen] = useState(false);
  const [preSelectedCategory, setPreSelectedCategory] =
    useState("Ask the community");
  const [preSelectedProduct, setPreSelectedProduct] = useState(null);

  // Fetch user profile data
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/profile", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setUserProfile(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setUserProfile(null);
      return null;
    }
  };

  const handleProfileClick = (user) => {
    if (!user || !user.id) return;
    navigate(`/profile/${user.id}`, { state: { user } });
  };

  // Fetch product details and set rating if user has rated before
  const fetchProductDetails = async (profile) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get(`/products/${id}`);
      const productDetails = response.data;
      setProductDetails(productDetails);

      // Check if the user has already rated the product
      if (profile && productDetails.product_reviews) {
        const userReview = productDetails.product_reviews.find(
          (review) => review.author.id === profile.id
        );
        if (userReview) {
          setCurrentValue(userReview.rate);
          setIsRatingSubmitted(true); // show "Rating submitted"
          console.log("User has rated before:", userReview.rate);
        } else {
          setCurrentValue(0);
          setIsRatingSubmitted(false);
          console.log("User has not rated before");
        }
      }
      return productDetails;
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Compare user skin types/concerns with product
  const compareProfileMatch = useCallback((product, profile) => {
    const matchedSkinTypes =
      profile.skin_type && product?.skin_types?.includes(profile.skin_type.id)
        ? [profile.skin_type.name]
        : [];

    const matchedSkinConcerns = profile.skin_concerns
      ?.filter((concern) => product?.skin_concerns?.includes(concern.id))
      .map((concern) => concern.name);

    setProfileMatch([...matchedSkinTypes, ...matchedSkinConcerns]);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch the user profile first, then the product details
      const profile = await fetchUserProfile();
      if (profile) {
        const product = await fetchProductDetails(profile);
        compareProfileMatch(product, profile);
      }
    };
    fetchData();
    // eslint-disable-next-line   react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async () => {
    setIsModalOpen(false);
    await fetchProductDetails();
    setIsRatingSubmitted(true);
  };

  if (isLoading) return <Spinner />;
  if (error) return <ProductNotFound error={error} />;

  const ingredientsToShow = showAll
    ? productDetails.ingredients
    : productDetails.ingredients.slice(0, 6);
  const reviewsToShow = productDetails.product_reviews?.slice(0, 2) || [];
  const postsToShow = productDetails.posts?.slice(0, 2) || [];

  return (
    <>
      <div className="w-100 pt-4">
        <FollowProvider>
          <div className="row d-flex flex-md-row">
            {/* Left Column  */}
            <div className="col-12 col-md-6 d-flex flex-column">
              {/* product-image module */}
              <div className="product-image mt-3 position-relative overflow-hidden">
                <img
                  src={
                    productDetails.image
                      ? productDetails.image
                      : "https://placehold.co/260x360/FFF/c2c2c2?text=No+Picture"
                  }
                  alt={productDetails.name}
                  className="img-fluid rounded mt-3 object-fit-cover"
                />
                <span className="wishlist-wrapper">
                  <WishListBtn
                    isAdd={productDetails.wishlist}
                    productId={productDetails.id}
                    onSuccess={fetchProductDetails}
                  />
                </span>
                <span
                  className="ask-community-wrapper"
                  onClick={() => {
                    setPreSelectedCategory("Ask the community");
                    setPreSelectedProduct(productDetails);
                    setIsPostModelOpen(true);
                  }}
                >
                  Ask community about this product
                </span>
              </div>

              {/* Ratings module */}
              <div className="ratings mt-5">
                <h5 className="section-title">Ratings</h5>
                <div className="row">
                  <div className="rating-summary col-lg-6 col-12 mb-4 mb-lg-0">
                    <h2 className="average-rating d-flex align-items-center">
                      <strong>{productDetails.rate}</strong>
                      <span className="text-muted">
                        <strong>/5 </strong>
                      </span>
                      <img
                        src={`/images/icons/Full star.svg`}
                        alt="rating star"
                        className="ms-2"
                      />
                    </h2>
                    <p className="rating-users">
                      {productDetails.rate_count} users rated
                    </p>
                    <RatingBreakdown
                      rateDetail={productDetails.rate_detail}
                      rateCount={productDetails.rate_count}
                    />
                  </div>

                  <RateProduct
                    productDetails={productDetails}
                    currentValue={currentValue}
                    setCurrentValue={setCurrentValue}
                    onRatingSubmit={handleSubmit}
                    isRatingSubmitted={isRatingSubmitted}
                    setIsRatingSubmitted={setIsRatingSubmitted}
                  />
                </div>
              </div>

              {/* Reviews module*/}
              <div className="reviews mt-5">
                <h5 className="section-title mb-3">Reviews</h5>
                {reviewsToShow.length > 0 ? (
                  reviewsToShow.map((review) => (
                    <ReviewItem
                      key={review.id}
                      review={review}
                      onProfileClick={handleProfileClick}
                    />
                  ))
                ) : (
                  <div className="review-item text-center border rounded shadow-sm d-flex flex-column justify-content-center">
                    <p className="text-muted">No reviews yet</p>
                  </div>
                )}

                {productDetails.product_reviews.length > 0 && (
                  <div className="text-center mt-3">
                    <button
                      className="btn btn-primary view-all-btn"
                      onClick={() => {
                        navigate(`/product/${id}/reviews`, {
                          state: {
                            productDetails,
                            currentValue,
                            isRatingSubmitted,
                            userProfile,
                          },
                        });
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      View all reviews
                    </button>
                  </div>
                )}
              </div>

              {/* What People Are Saying module */}
              <div className="what-people-are-saying mt-5">
                <h5 className="section-title mb-3">What People Are Saying</h5>
                {postsToShow.length > 0 ? (
                  postsToShow.map((post) => (
                    <CuruChatsPost
                      key={post.id}
                      post={post}
                      onProfileClick={handleProfileClick}
                    />
                  ))
                ) : (
                  <div className="post-item text-center border rounded shadow-sm d-flex flex-column justify-content-center">
                    <p className="text-muted">No post yet</p>
                  </div>
                )}

                {productDetails.posts.length > 0 && (
                  <div className="text-center mt-3">
                    <button
                      className="btn btn-primary view-all-btn"
                      onClick={() => {
                        navigate(`/product/${id}/posts`, {
                          state: { productDetails },
                        });
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      View all
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="col-12 col-md-6 d-flex flex-column">
              {/* Product details module*/}
              <div className="product-details el mt-4">
                {/* product name and subtitle*/}
                <h3 className="product-title">{productDetails.name}</h3>
                <div className="product-subtitle">
                  <p>
                    {productDetails.category?.name}
                    {productDetails.sub_category &&
                      ` / ${productDetails.sub_category.name}`}
                    {productDetails.sub_heading &&
                    typeof productDetails.sub_heading === "string"
                      ? ` / ${productDetails.sub_heading}`
                      : ""}
                  </p>
                </div>

                {/* Rating info */}
                <div className="rating-info d-none d-md-block d-flex align-items-center mb-4">
                  <span className="rating-stars">
                    <CustomRating rate={productDetails.rate} />
                  </span>
                  <span className="ms-2">
                    {productDetails.rate}/5 ({productDetails.rate_count}{" "}
                    ratings)
                  </span>
                </div>

                {/* Curu match */}
                <div className="curu-match d-flex align-items-center border border-2 rounded-2 w-90 p-3 my-3">
                  <div className="curu-progress-icon me-4">
                    <Progress
                      type="circle"
                      percent={productDetails.curu_match}
                      size={60}
                      strokeColor="#93E4D0"
                      trailColor="#D9D9D9"
                      strokeWidth={10}
                      format={() => (
                        <img
                          src={`/images/icons/star.svg`}
                          alt="star"
                          className="curu-match-progress-icon-img"
                        />
                      )}
                    />
                  </div>
                  <div className="curu-match-details">
                    <h5 className="section-title mt-3">
                      {productDetails.curu_match}% Curu match
                    </h5>
                    <CuruMatchDescription
                      curuMatch={productDetails.curu_match}
                    />
                  </div>
                </div>

                {/* Product Highlights */}
                <div className="highlights mt-4">
                  <h5 className="section-title">Product Highlights</h5>
                  <div className="d-flex flex-wrap gap-4 justify-content-start mt-3">
                    {productDetails.benefits.map((benefit) => (
                      <div
                        key={benefit.id}
                        className="highlight-item d-flex flex-column align-items-center text-center"
                      >
                        <div className="feature-icon d-inline-flex align-items-center justify-content-center fs-2 mb-1 rounded-circle">
                          <img
                            src={`${process.env.PUBLIC_URL}/images/icons/${benefit.name}.svg`}
                            alt=""
                            className="highlight-image"
                          />
                        </div>
                        <span className="highlight-name">{benefit.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Profile Match Section */}
                <div className="profile-match mt-5">
                  <h5 className="section-title">Your profile match</h5>
                  <div className="d-flex flex-wrap gap-3 justify-content-start mt-3">
                    {profileMatch.length > 0 ? (
                      profileMatch.map((match, index) => (
                        <div
                          key={index}
                          className="profile-match-container d-flex align-items-center px-3 py-2 border border-1 rounded-pill fs-6"
                        >
                          <img
                            src={`/images/icons/Green check.svg`}
                            alt={match}
                            className="profile-match-icon me-2"
                          />
                          <span className="profile-match-data">{match}</span>
                        </div>
                      ))
                    ) : (
                      <p> No matching skin types or concerns</p>
                    )}
                  </div>
                </div>

                {/* Ingredients */}
                <div className="ingredients">
                  <h5 className="section-title">Ingredients</h5>
                  <div className="d-flex flex-wrap gap-3 justify-content-start mt-3">
                    {ingredientsToShow.map((ingredient) => (
                      <span
                        key={ingredient.id}
                        className="ingredient-badge rounded-pill text-body px-3 py-2 fs-6"
                      >
                        {ingredient.name}
                      </span>
                    ))}
                  </div>

                  {productDetails.ingredients.length > 6 && (
                    <div className="d-flex justify-content-center mt-3">
                      <button
                        className="show-all-button"
                        onClick={() => setShowAll(!showAll)}
                      >
                        {showAll ? "Show less" : "+ Show all"}
                      </button>
                    </div>
                  )}
                </div>
                {/* More from the brand module */}
              <div className="more-from-the-brand">
                <h5 className="section-title mb-3">More from the Brand</h5>
                <div className="brand-item">
                  {/* For larger screens */}
                  <div className="card d-none d-md-block border rounded shadow-sm overflow-hidden w-75">
                    <div className="row">
                      <div className="col-5 d-flex justify-content-center align-items-center">
                        <a
                          href={productDetails.brand.site}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="brand-image-link d-flex justify-content-center align-items-center w-100 h-100 position-relative"
                        >
                          {productDetails.brand.image ? (
                            <img
                              src={productDetails.brand.image}
                              alt={productDetails.brand.name}
                              className="img-fluid brand-image ms-3"
                            />
                          ) : (
                            <span className="text-muted bold fs-4 ms-3">
                              {productDetails.brand.name}
                            </span>
                          )}
                        </a>
                      </div>

                      <div className="col-7 d-flex flex-column justify-content-center card-body">
                        <h6 className="fw-bold fs-4">
                          {productDetails.brand.name}
                        </h6>
                        <p className="mb-4 text-muted fs-6">
                          {productDetails.brand.product_count} products
                        </p>
                        <Link
                          to={`/brand/${productDetails.brand.id}`}
                          className="brand-page-link text-dark text-decoration-none"
                        >
                          View products &gt;
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Mobile screens */}
                  <div className="store-card border rounded shadow-sm overflow-hidden row g-0 d-md-none w-100">
                    <div className="col-5 d-flex justify-content-center align-items-center">
                      <a
                        href={productDetails.brand.site}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="brand-image-link"
                      >
                        {productDetails.brand.image ? (
                          <img
                            src={productDetails.brand.image}
                            alt={productDetails.brand.name}
                            className="img-fluid brand-image ms-3"
                          />
                        ) : (
                          <div
                            className="text-center text-muted d-flex align-items-center justify-content-center"
                            style={{ fontSize: "1.5rem" }}
                          >
                            {" "}
                            {productDetails.brand.name}
                          </div>
                        )}
                      </a>
                    </div>

                    <div className="col-5 d-flex flex-column justify-content-center">
                      <h5 className="store-name">
                        {productDetails.brand.name}
                      </h5>
                      <p className="mb-4 text-muted fs-6">
                        {productDetails.brand.product_count} products
                      </p>
                    </div>

                    <div className="col-2 d-flex align-items-center justify-content-center">
                      <span className="store-arrow">&gt;</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Where to buy module*/}
            <div className="where-to-buy">
              </div>

              
                <h5 className="section-title mb-3">Where to Buy</h5>

                <div className="d-flex flex-wrap gap-3">
                  {productDetails.stores?.length > 0 ? (
                    productDetails.stores.map((store) => {
                      const bg = store.image || store.logo || "";
                      const siteLabel = store.site
                        ? store.site.replace(/^https?:\/\//, "")
                        : "";

                      return (
                        <a
                          key={store.id}
                          href={store.site}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          <div
                            className="store-card where-to-buy-item"
                            style={{ backgroundImage: `url(${bg})` }}
                          >
                            <div className="store-details w-100 d-flex align-items-center px-3">
                              <div className="flex-grow-1 overflow-hidden">
                                <div className="store-name">
                                  {store.name || "Shop name"}
                                </div>
                                <div className="store-site site-ellips">
                                  {siteLabel}
                                </div>
                              </div>
                              <span className="store-arrow">&gt;</span>
                            </div>
                          </div>
                        </a>
                      );
                    })
                  ) : (
                    <p className="text-muted">No store links available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </FollowProvider>
      </div>
      {isPostModelOpen && (
        <PostModel
          profile={userProfile}
          isOpen={isPostModelOpen}
          onClose={() => setIsPostModelOpen(false)}
          preSelectedCategory={preSelectedCategory}
          preSelectedProduct={preSelectedProduct}
        />
      )}
    </>
  );
}
